import { styled, alpha, type Theme } from '@mui/material/styles';
import { Box, TextField, Autocomplete, IconButton, Typography } from '@mui/material';

export const ProductFormContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  marginBottom: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  '&:focus-within': {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.2)}`,
  },
}));

export const RemoveButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.5),
  borderRadius: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  width: '40px',
  height: '40px',
  marginTop: '18px', // Centrado más arriba, alineado con el centro del input
  flexShrink: 0,
  alignSelf: 'flex-start',
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '15',
    border: 'none',
  },
  '&:active': {
    transform: 'scale(0.92)',
  },
  '&::before': {
    display: 'none',
  },
  '&::after': {
    display: 'none',
  },
}));

export const FormFieldsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto', // Producto flexible, Cantidad con botón
  gap: theme.spacing(1.5),
  alignItems: 'start',
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const QuantityContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  minWidth: '140px',
  position: 'relative',
  '&::before': {
    display: 'none',
  },
  '&::after': {
    display: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem', // text-sm
    '& fieldset': {
      borderColor: alpha(theme.palette.success.main, 0.45),
    },
    '&:hover fieldset': {
      borderColor: theme.palette.success.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0BB296',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem', // text-sm
    '&.Mui-focused': {
      color: theme.palette.success.main,
    },
  },
}));

const inputSurface = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? {
        backgroundColor: alpha(theme.palette.common.white, 0.06),
        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.1),
          '& fieldset': {
            borderColor: theme.palette.success.main,
          },
        },
        '&.Mui-focused': {
          backgroundColor: alpha(theme.palette.common.white, 0.12),
          '& fieldset': {
            borderColor: '#0BB296',
            borderWidth: '2px',
          },
        },
      }
    : {
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
          backgroundColor: theme.palette.background.paper,
          '& fieldset': {
            borderColor: theme.palette.success.main,
          },
        },
        '&.Mui-focused': {
          backgroundColor: theme.palette.background.paper,
          '& fieldset': {
            borderColor: '#0BB296',
            borderWidth: '2px',
          },
        },
      };

export const StyledAutocomplete = styled(Autocomplete<{ id: number; name: string; code: string }>)(
  ({ theme }) => ({
    '& .MuiOutlinedInput-root': {
      fontSize: '0.9375rem',
      borderRadius: '10px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: alpha(theme.palette.success.main, 0.45),
        borderWidth: '1px',
      },
      ...inputSurface(theme),
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      fontWeight: 400,
      transition: theme.transitions.create(['color', 'transform'], {
        duration: theme.transitions.duration.shorter,
      }),
      '&.Mui-focused': {
        color: theme.palette.success.main,
        fontWeight: 500,
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '0.9375rem',
      padding: theme.spacing(1.25, 1.5),
    },
    // Ensure label animation works when value is present
    '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
      paddingRight: '14px',
    },
  }),
);

export const QuantityField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    fontSize: '0.9375rem',
    borderRadius: '10px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '& fieldset': {
      borderColor: alpha(theme.palette.success.main, 0.45),
      borderWidth: '1px',
    },
    ...inputSurface(theme),
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
    '&.Mui-focused': {
      color: theme.palette.success.main,
      fontWeight: 500,
    },
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontWeight: 500,
  },
  '& .MuiFormHelperText-root': {
    margin: 0,
    marginTop: '4px',
  },
}));

export const ProductCodeField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem', // text-sm
    backgroundColor: theme.palette.background.paper,
    '& fieldset': {
      borderColor: alpha(theme.palette.success.main, 0.45),
    },
    '&:hover fieldset': {
      borderColor: theme.palette.success.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0BB296',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem', // text-sm
    '&.Mui-focused': {
      color: theme.palette.success.main,
    },
  },
}));

export const ErrorMessage = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 400,
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
  padding: 0,
  backgroundColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const SuccessMessage = styled(Typography)(({ theme }) => ({
  display: 'none', // OCULTO COMPLETAMENTE
}));

export const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  borderRadius: '10px',
  border: `1px dashed ${theme.palette.divider}`,
  gridColumn: '1 / -1',
  opacity: 0.7,
  backgroundColor: theme.palette.background.paper,
}));
